import React, { useEffect, useState } from 'react';
import {
    Card,
    Table,
    Form,
    Button,
    Pagination,
    Row,
    Col,
    Badge, 
    Spinner,
} from 'react-bootstrap';
import {
    getUsers,
    getSalaries,
    addSalary,
    getPettyCashDetails,
    getStaffBatches,
    getStaffLeaves,
    getAllStaffLeaves,
    updateLeaveStatus,
    getAttendance,
} from '../../service/apiService';
import './ManageStaff.css';

function ManageStaff() {
    const [staffList, setStaffList] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [loading, setLoading] = useState(true);

    const itemsPerPage = 5;

    // Salary-related states
    const [salaryForm, setSalaryForm] = useState({
        month_year: '',
        basic: '',
        hra: '',
        allowances: '',
        deductions: '',
        remarks: '',
    });
    const [salaries, setSalaries] = useState([]);
    const [saving, setSaving] = useState(false);

    // Additional info
    const [pettyCash, setPettyCash] = useState(null);
    const [batches, setBatches] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [allLeaves, setAllLeaves] = useState([]);
    const [attendance, setAttendance] = useState([]);

    // --- Fetch staff list ---
    useEffect(() => {
        const fetchStaff = async () => {
            setLoading(true);
            try {
                const data = await getUsers();
                const staffOnly = data.filter((u) => u.role !== 'student');
                setStaffList(staffOnly);
                setFiltered(staffOnly);
            } catch (error) {
                console.error('Error fetching staff:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStaff();
        loadAllLeaves();
    }, []);

    const leaveStatusBadge = (status) => {
        const variants = {
            approved: 'success',
            rejected: 'danger',
            pending: 'warning',
        };
        return <Badge bg={variants[status] || 'secondary'}>{status || 'pending'}</Badge>;
    };

    const loadAllLeaves = async () => {
        try {
            const data = await getAllStaffLeaves();
            setAllLeaves(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching leave requests:', error);
            setAllLeaves([]);
        }
    };

    // --- Search & filter ---
    useEffect(() => {
        let result = staffList;
        if (search) {
            result = result.filter((s) =>
                s.name.toLowerCase().includes(search.toLowerCase())
            );
        }
        if (roleFilter) {
            result = result.filter((s) => s.role === roleFilter);
        }
        setFiltered(result);
        setCurrentPage(1);
    }, [search, roleFilter, staffList]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const displayed = filtered.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) setCurrentPage(page);
    };

    // --- Salary Management ---
    const loadSalaries = async (staffId) => {
        const data = await getSalaries(staffId);
        setSalaries(data || []);
    };

    useEffect(() => {
        if (selectedStaff) {
            loadSalaries(selectedStaff.id);
            loadStaffDetails(selectedStaff.id);
        }
    }, [selectedStaff]);

    const handleSalaryChange = (e) => {
        setSalaryForm({ ...salaryForm, [e.target.name]: e.target.value });
    };

    const handleMarkPaid = async () => {
        if (!salaryForm.month_year) return alert('Please select a month!');
        setSaving(true);
        const payload = {
            staff_id: selectedStaff.id,
            month_year: salaryForm.month_year,
            basic: parseFloat(salaryForm.basic || 0),
            hra: parseFloat(salaryForm.hra || 0),
            allowances: parseFloat(salaryForm.allowances || 0),
            deductions: parseFloat(salaryForm.deductions || 0),
            remarks: salaryForm.remarks || '',
            status: 'paid',
        };
        const res = await addSalary(payload);
        setSaving(false);
        if (res.status === 'success') {
            alert('Salary marked paid & slip generated!');
            setSalaryForm({
                month_year: '',
                basic: '',
                hra: '',
                allowances: '',
                deductions: '',
                remarks: '',
            });
            loadSalaries(selectedStaff.id);
        } else {
            alert(res.message || 'Error saving salary');
        }
    };

    // --- Additional Data Loaders ---
    const loadStaffDetails = async (staffId) => {
        const [cash, batch, leaveList, attendanceData] = await Promise.all([
            getPettyCashDetails(staffId),
            getStaffBatches(staffId),
            getStaffLeaves(staffId),
            getAttendance(staffId),
        ]);
        setPettyCash(cash);
        setBatches(batch);
        setLeaves(leaveList);
        setAttendance(Array.isArray(attendanceData) ? attendanceData : []);
    };

    const handleLeaveAction = async (id, status) => {
        const res = await updateLeaveStatus(id, status);
        if (res.status === 'success') {
            alert(`Leave ${status}`);
            await loadAllLeaves();
            if (selectedStaff?.id) loadStaffDetails(selectedStaff.id);
        } else {
            alert(res.message || 'Unable to update leave status');
        }
    };

    // --- PROFILE VIEW ---
    if (selectedStaff) {
        const staff = selectedStaff;

        return (
            <div className="manage-staff-page">
                <Card className="shadow-sm p-4 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>
                            {staff.name}{' '}
                            <small className="text-muted">
                                ({staff.role.toUpperCase()} / {staff.position})
                            </small>
                        </h4>
                        <Button variant="secondary" onClick={() => setSelectedStaff(null)}>
                            ← Back to List
                        </Button>
                    </div>

                    <Row>
                        <Col md={6}>
                            <p>
                                <strong>Email:</strong> {staff.email}
                            </p>
                            <p>
                                <strong>Phone:</strong> {staff.phone || '-'}
                            </p>
                            <p>
                                <strong>Joined On:</strong> {staff.join_date || 'N/A'}
                            </p>
                        </Col>
                        <Col md={6}>
                            <p>
                                <strong>Department:</strong> {staff.department || '-'}
                            </p>
                            <p>
                                <strong>Salary:</strong> ₹{staff.salary || 'N/A'}
                            </p>
                            <p>
                                <strong>Status:</strong>{' '}
                                <Badge
                                    bg={staff.status === 'active' ? 'success' : 'secondary'}
                                    className="ms-1"
                                >
                                    {staff.status || 'active'}
                                </Badge>
                            </p>
                        </Col>
                    </Row>

                    <hr />
                    <h5>Additional Details</h5>
                    <ul>
                        <li>Leave Balance: {staff.leave_balance || 10} days</li>
                        <li>
                            Petty Cash Access:{' '}
                            {staff.role === 'office admin' ? 'Yes' : 'No'}
                        </li>
                        <li>Assigned Students: {staff.assigned_students || 0}</li>
                        <li>Attendance This Month: {staff.attendance_days || 0} days</li>
                    </ul>

                    {/* Role Specific */}
                    <hr />

                    {staff.position === 'Office Staff' && pettyCash && (
                        <>
                            <h5 className="mt-3">Petty Cash Summary</h5>
                            <p>
                                <strong>Current Balance:</strong>{' '}
                                <span className="text-success fw-bold">
                                    ₹ {Number(
                                        pettyCash?.length > 0
                                            ? parseFloat(pettyCash[0].balance_after) || 0
                                            : 0
                                    ).toFixed(2)}

                                </span>
                            </p>
                            <Table bordered hover size="sm" className="mt-3">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Category</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>Remarks</th>
                                        <th>Balance After</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pettyCash.length > 0 ? (
                                        pettyCash.slice(0, 10).map((txn) => (
                                            <tr key={txn.id}>
                                                <td>{new Date(txn.txn_date).toLocaleString()}</td>
                                                <td>
                                                    {txn.txn_type === 'add' ? (
                                                        <span className="text-success">+ Add</span>
                                                    ) : (
                                                        <span className="text-danger">- Expense</span>
                                                    )}
                                                </td>
                                                <td>{txn.category || '-'}</td>
                                                <td>{txn.description || '-'}</td>
                                                <td>₹ {Number(txn.amount).toFixed(2)}</td>
                                                <td>{txn.remarks || '-'}</td>
                                                <td>₹ {Number(txn.balance_after).toFixed(2)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center text-muted">
                                                No petty cash transactions found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </>
                    )}


                    {staff.position === 'Trainer' && batches && (
                        <>
                            <h5 className="mt-3">Assigned Batches</h5>
                            {batches.length > 0 ? (
                                <Table bordered hover size="sm" className="mt-3">
                                    <thead>
                                        <tr>
                                            <th>Batch</th>
                                            <th>Subject</th>
                                            <th>Students</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batches.map((b) => (
                                            <tr key={b.id}>
                                                <td>{b.batch_name}</td>
                                                <td>{b.subject}</td>
                                                <td>{b.students_count}</td>
                                                <td>{b.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted">No batches assigned.</p>
                            )}
                        </>
                    )}

                    {/* Salary Section */}
                    <hr />
                    <h5 className="mt-3">Salary Management</h5>
                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <Form.Label>Month</Form.Label>
                            <Form.Control
                                type="month"
                                name="month_year"
                                value={salaryForm.month_year}
                                onChange={handleSalaryChange}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label>Basic</Form.Label>
                            <Form.Control
                                name="basic"
                                type="number"
                                value={salaryForm.basic}
                                onChange={handleSalaryChange}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label>HRA</Form.Label>
                            <Form.Control
                                name="hra"
                                type="number"
                                value={salaryForm.hra}
                                onChange={handleSalaryChange}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label>Allowances</Form.Label>
                            <Form.Control
                                name="allowances"
                                type="number"
                                value={salaryForm.allowances}
                                onChange={handleSalaryChange}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label>Deductions</Form.Label>
                            <Form.Control
                                name="deductions"
                                type="number"
                                value={salaryForm.deductions}
                                onChange={handleSalaryChange}
                            />
                        </Col>
                        <Col md={12}>
                            <Form.Label>Remarks</Form.Label>
                            <Form.Control
                                name="remarks"
                                value={salaryForm.remarks}
                                onChange={handleSalaryChange}
                            />
                        </Col>
                        <Col md={12}>
                            <Button
                                variant="primary"
                                onClick={handleMarkPaid}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Mark Paid & Generate Slip'}
                            </Button>
                        </Col>
                    </Row>

                    {/* Salary Records */}
                    <Card className="shadow-sm p-3 mt-4">
                        <h6 className="mb-3">Salary Records</h6>
                        {salaries.length === 0 ? (
                            <p className="text-muted">No salary records yet.</p>
                        ) : (
                            <Table bordered hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th>Gross</th>
                                        <th>Net</th>
                                        <th>Status</th>
                                        <th>Paid On</th>
                                        <th>Slip</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salaries.map((r) => (
                                        <tr key={r.id}>
                                            <td>{r.month_year}</td>
                                            <td>₹ {Number(r.gross).toFixed(2)}</td>
                                            <td>₹ {Number(r.net).toFixed(2)}</td>
                                            <td>
                                                <Badge
                                                    bg={r.status === 'paid' ? 'success' : 'secondary'}
                                                >
                                                    {r.status}
                                                </Badge>
                                            </td>
                                            <td>{r.paid_on || '-'}</td>
                                            <td>
                                                {r.slip_path ? (
                                                    <a
                                                        href={`https://crm.semicoloninnovations.in/api/${r.slip_path}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Download
                                                    </a>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card>

                    {/* Leaves */}
                    <hr />
                    <h5 className="mt-4">Leave Requests</h5>
                    <Table bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Reason</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaves.length > 0 ? (
                                leaves.map((l) => (
                                    <tr key={l.id}>
                                        <td>{l.leave_date}</td>
                                        <td>{l.reason}</td>
                                        <td>{leaveStatusBadge(l.status)}</td>
                                        <td>
                                            {l.status === 'pending' ? (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        variant="success"
                                                        className="me-2"
                                                        onClick={() => handleLeaveAction(l.id, 'approved')}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        onClick={() => handleLeaveAction(l.id, 'rejected')}
                                                    >
                                                        Reject
                                                    </Button>
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center text-muted">
                                        No leave requests found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {/* Attendance */}
                    <hr />
                    <h5 className="mt-4">Attendance (Last 30 Days)</h5>
                    <Table bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Login</th>
                                <th>Logout</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attendance.length > 0 ? (
                                attendance.map((a) => (
                                    <tr key={a.id}>
                                        <td>{a.date}</td>
                                        <td>{a.status}</td>
                                        <td>{a.login_time || '-'}</td>
                                        <td>{a.logout_time || '-'}</td>
                                        <td>{a.remarks || '-'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted">
                                        No attendance data
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card>
            </div>
        );
    }

    // --- DEFAULT: Staff List ---
    return (
        <div className="manage-staff-page">
            <h3 className="mb-4">Manage Staff</h3>
            <Card className="shadow-sm p-3 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Leave Requests</h5>
                    <Badge bg="warning" text="dark">
                        {allLeaves.filter((leave) => leave.status === 'pending').length} Pending
                    </Badge>
                </div>
                <Table bordered hover responsive size="sm" className="mb-0">
                    <thead>
                        <tr>
                            <th>Staff</th>
                            <th>Date</th>
                            <th>Reason</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allLeaves.length > 0 ? (
                            allLeaves.map((leave) => (
                                <tr key={leave.id}>
                                    <td>
                                        <strong>{leave.staff_name || '-'}</strong>
                                        <div className="text-muted small">{leave.staff_email || ''}</div>
                                    </td>
                                    <td>{leave.leave_date}</td>
                                    <td>{leave.reason}</td>
                                    <td>{leaveStatusBadge(leave.status)}</td>
                                    <td>
                                        {leave.status === 'pending' ? (
                                            <div className="d-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="success"
                                                    onClick={() => handleLeaveAction(leave.id, 'approved')}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => handleLeaveAction(leave.id, 'rejected')}
                                                >
                                                    Reject
                                                </Button>
                                            </div>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center text-muted">
                                    No leave requests found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Card>
            <Card className="shadow-sm p-3 mb-4">
                <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
                    <Form.Control
                        type="text"
                        placeholder="Search by name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ maxWidth: '250px' }}
                    />
                    <Form.Select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        style={{ maxWidth: '250px' }}
                    >
                        <option value="">All Roles</option>
                        <option value="staff">Staff</option>
                        <option value="office admin">Office Admin</option>
                        <option value="trainer">Trainer</option>
                    </Form.Select>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Role</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayed.length > 0 ? (
                                    displayed.map((s, i) => (
                                        <tr
                                            key={s.id}
                                            onClick={() => setSelectedStaff(s)}
                                            className="table-row-clickable"
                                        >
                                            <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                                            <td>{s.name}</td>
                                            <td>{s.role}</td>
                                            <td>{s.email}</td>
                                            <td>{s.phone || '-'}</td>
                                            <td>
                                                <Badge
                                                    bg={s.status === 'active' ? 'success' : 'secondary'}
                                                >
                                                    {s.status || 'active'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            No staff found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center mt-3">
                                <Pagination>
                                    <Pagination.Prev
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    />
                                    {[...Array(totalPages)].map((_, i) => (
                                        <Pagination.Item
                                            key={i + 1}
                                            active={i + 1 === currentPage}
                                            onClick={() => handlePageChange(i + 1)}
                                        >
                                            {i + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}

export default ManageStaff;
